/**
 * @file
 * Language switcher functionality.
 *
 * Handles the desktop dropdown and mobile slide-in panels, plus a
 * "best language" suggestion that runs only when a panel is opened.
 * Detection combines a MaxMind GeoIP2 IP lookup (client-side JS client,
 * authorized by domain) with the browser language. The result is cached in
 * sessionStorage so MaxMind is queried at most once per session.
 */

import { hide, show } from 'slidetoggle';
import { slideToggleDefaults } from '../../modules/_SlideToggle';

(function (Drupal, drupalSettings, once) {
  'use strict';

  // Session storage key for the resolved suggestion.
  const CACHE_KEY = 'wfLangSuggestion';

  // MaxMind GeoIP2 JavaScript client. Must be served from MaxMind's servers.
  const GEOIP_SRC = 'https://geoip-js.com/js/apis/geoip2/v2.1/geoip2.js';

  // Countries mapped to the European (UK English) site. Everything else
  // defaults to North America.
  const EUROPE_COUNTRIES = new Set([
    'GB', 'IE', 'FR', 'DE', 'ES', 'IT', 'NL', 'BE', 'LU', 'PT', 'AT', 'CH',
    'DK', 'SE', 'NO', 'FI', 'IS', 'PL', 'CZ', 'SK', 'HU', 'RO', 'BG', 'GR',
    'HR', 'SI', 'EE', 'LV', 'LT', 'MT', 'CY', 'LI', 'MC', 'AD', 'SM', 'VA',
  ]);

  // Resolved suggestion for this page load (a language object) once known.
  let suggestion = null;
  // Localized names of the visitor's country from MaxMind, once known.
  let countryNames = null;
  // Promise tracking the (single) MaxMind script load.
  let geoipLoading = null;
  // True while a live lookup is in flight, to avoid duplicate lookups when the
  // panel is opened again before the first one resolves.
  let detecting = false;

  /**
   * Returns the language switcher settings, or null when unavailable.
   */
  function getSettings() {
    const settings = drupalSettings.wfLanguageSwitcher;
    if (!settings || !settings.languages || Object.keys(settings.languages).length < 2) {
      return null;
    }
    return settings;
  }

  /**
   * Maps an ISO country code to a region key.
   */
  function regionForCountry(countryCode) {
    return EUROPE_COUNTRIES.has((countryCode || '').toUpperCase()) ? 'Europe' : 'North America';
  }

  /**
   * Maps geographic coordinates to a region key.
   *
   * Used by the native Geolocation fallback, which returns latitude/longitude
   * rather than a country. A rough European bounding box is enough for the same
   * binary Europe vs North America split that regionForCountry() makes.
   */
  function regionForCoords(lat, lng) {
    if (lat >= 34 && lat <= 72 && lng >= -25 && lng <= 45) {
      return 'Europe';
    }
    return 'North America';
  }

  /**
   * Determines the desired langcode from a region and the browser language.
   */
  function wantLangcode(region, browserLang) {
    browserLang = (browserLang || '').toLowerCase();
    if (region === 'Europe') {
      return 'en-gb';
    }
    // North America: refine the variant by browser language.
    if (browserLang.indexOf('fr') === 0) {
      return 'fr';
    }
    if (browserLang.indexOf('es') === 0) {
      return 'es';
    }
    return 'en';
  }

  /**
   * Resolves a desired langcode to an available language, with fallbacks.
   */
  function resolve(settings, region, want) {
    const langs = settings.languages;
    const regionEnglish = region === 'Europe' ? 'en-gb' : 'en';
    const chain = [want, regionEnglish, 'en', settings.current];

    for (let i = 0; i < chain.length; i++) {
      const code = chain[i];
      if (code && langs[code]) {
        return langs[code];
      }
    }

    // Last resort: the first available language.
    const keys = Object.keys(langs);
    return keys.length ? langs[keys[0]] : null;
  }

  /**
   * Browser-language-only resolution, used when the IP lookup is unavailable.
   */
  function resolveBrowserOnly(settings) {
    const bl = (navigator.language || '').toLowerCase();
    let region = 'North America';
    let want = 'en';

    if (bl.indexOf('en-gb') === 0) {
      region = 'Europe';
      want = 'en-gb';
    }
    else if (bl.indexOf('fr') === 0) {
      want = 'fr';
    }
    else if (bl.indexOf('es') === 0) {
      want = 'es';
    }

    return resolve(settings, region, want);
  }

  /**
   * Toggles the loading message vs the suggestion button.
   *
   * @param {string} state
   *   'loading' to show the message and hide the button, 'ready' for the
   *   opposite.
   */
  function setSuggestionState(state) {
    const ready = state === 'ready';
    document.querySelectorAll('.js-language-suggestion-loading').forEach(function (el) {
      el.hidden = ready;
    });
    document.querySelectorAll('.js-language-suggestion').forEach(function (btn) {
      btn.hidden = !ready;
    });
  }

  /**
   * Updates every suggestion button's label and href, without revealing it.
   */
  function setSuggestionContent(lang) {
    if (!lang) {
      return;
    }
    document.querySelectorAll('.js-language-suggestion').forEach(function (btn) {
      btn.textContent = lang.display;
      btn.setAttribute('href', lang.url);
    });
  }

  /**
   * Names the visitor's country in the intro title.
   */
  function setIntroCountry(names) {
    const lang = (drupalSettings.path.currentLanguage || 'en').split('-')[0];
    const name = names && (names[lang] || names.en);
    if (!name) {
      return;
    }
    document.querySelectorAll('.js-language-intro-title').forEach(function (el) {
      el.innerHTML = Drupal.t('It looks like you’re in @country', { '@country': name });
    });
  }

  /**
   * Updates every suggestion button with the detected language and reveals it.
   */
  function applySuggestion(lang) {
    setIntroCountry(countryNames);
    setSuggestionContent(lang);
    // Whether or not a language resolved, reveal the button (with its
    // server-rendered default when null) so the loading message never sticks.
    setSuggestionState('ready');
  }

  /**
   * Stores and applies a resolved suggestion.
   */
  function finish(lang, names) {
    if (!lang) {
      return;
    }
    suggestion = lang;
    countryNames = names || countryNames;
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        langcode: lang.langcode,
        country: countryNames,
      }));
    }
    catch (e) {
      // Ignore storage failures (private mode, quota, etc.).
    }
    applySuggestion(lang);
  }

  /**
   * Shows/hides the "Find my location" button.
   */
  function toggleGeolocate(show) {
    document.querySelectorAll('.js-language-geolocate').forEach(function (btn) {
      btn.hidden = !show;
    });
  }

  /**
   * Falls back to the manual geolocation button when the IP lookup is
   * unavailable (e.g. Firefox blocks the MaxMind script by default). The guessed
   * browser-language suggestion is NOT shown — instead the user gets an explicit,
   * consent-based way to detect their region. The (hidden) suggestion button is
   * still primed with the browser-language result as a last resort for browsers
   * without the Geolocation API.
   */
  function geolocateFallback(lang) {
    setSuggestionContent(lang);
    document.querySelectorAll('.js-language-suggestion-loading').forEach(function (el) {
      el.hidden = true;
    });
    if ('geolocation' in navigator) {
      // Hide the guessed suggestion; offer manual geolocation instead.
      document.querySelectorAll('.js-language-suggestion').forEach(function (btn) {
        btn.hidden = true;
      });
      toggleGeolocate(true);
    }
    else {
      // No Geolocation API — reveal the browser-language suggestion as a last
      // resort so the user isn't left with nothing.
      setSuggestionState('ready');
    }
  }

  /**
   * Sets the in-progress label on the geolocation button (disabled), stashing
   * the original label so it can be restored on error.
   */
  function setGeolocateBusy(text) {
    document.querySelectorAll('.js-language-geolocate').forEach(function (btn) {
      if (btn.dataset.label === undefined) {
        btn.dataset.label = btn.textContent.trim();
      }
      btn.textContent = text;
      btn.disabled = true;
    });
  }

  /**
   * Restores the geolocation button's original label and re-enables it.
   */
  function resetGeolocate() {
    document.querySelectorAll('.js-language-geolocate').forEach(function (btn) {
      if (btn.dataset.label !== undefined) {
        btn.textContent = btn.dataset.label;
      }
      btn.disabled = false;
    });
  }

  /**
   * Shows an error message in the status line beneath the button.
   */
  function setGeolocateError(message) {
    document.querySelectorAll('.js-language-geolocate-status').forEach(function (el) {
      el.textContent = message || '';
      el.hidden = !message;
    });
  }

  /**
   * Requests the user's location via the native Geolocation API and updates the
   * suggestion accordingly. Triggered by the "Find my location" button, so the
   * browser permission prompt is tied to an explicit user action.
   */
  function geolocate() {
    const settings = getSettings();
    if (!settings || !('geolocation' in navigator)) {
      return;
    }
    setGeolocateError('');
    setGeolocateBusy(Drupal.t('Finding your location…'));
    navigator.geolocation.getCurrentPosition(
      function (position) {
        const region = regionForCoords(position.coords.latitude, position.coords.longitude);
        const lang = resolve(settings, region, wantLangcode(region, navigator.language));
        // Reveal the resolved suggestion and hide the (now done) button silently.
        finish(lang);
        resetGeolocate();
        toggleGeolocate(false);
      },
      function (error) {
        // 1 = permission denied (often a remembered "block"), 2 = position
        // unavailable, 3 = timeout. Tailor the message so the user knows whether
        // to change a browser setting or just pick a region manually.
        let message;
        if (error && error.code === 1) {
          message = Drupal.t('Location access is blocked. Allow it for this site in your browser, or choose a language manually.');
        }
        else {
          message = Drupal.t('We could not determine your location. Please choose a language manually.');
        }
        resetGeolocate();
        setGeolocateError(message);
      },
      // Coarse Europe-vs-North-America accuracy is all we need, so skip GPS
      // (enableHighAccuracy: false) and accept any cached fix the browser
      // already has (maximumAge: Infinity) to return instantly when possible.
      { enableHighAccuracy: false, timeout: 15000, maximumAge: Infinity }
    );
  }

  /**
   * Lazily injects the MaxMind GeoIP2 script (once).
   */
  function loadGeoip2() {
    if (window.geoip2) {
      return Promise.resolve();
    }
    if (geoipLoading) {
      return geoipLoading;
    }
    geoipLoading = new Promise(function (resolve, reject) {
      const script = document.createElement('script');
      script.src = GEOIP_SRC;
      script.async = true;
      script.onload = function () {
        resolve();
      };
      script.onerror = function () {
        reject(new Error('Failed to load MaxMind GeoIP2 client.'));
      };
      document.head.appendChild(script);
    });
    return geoipLoading;
  }

  /**
   * Runs detection when a panel opens and updates the suggestion button.
   *
   * Order: in-memory result -> session cache -> MaxMind IP + browser language
   * -> browser language only (on lookup failure).
   */
  function detectAndSuggest() {
    const settings = getSettings();
    if (!settings) {
      // Block barely rendered — reveal the default button just in case.
      setSuggestionState('ready');
      return;
    }

    // Already resolved during this page load.
    if (suggestion) {
      applySuggestion(suggestion);
      return;
    }

    // Resolved earlier this session.
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (settings.languages[parsed.langcode]) {
          finish(settings.languages[parsed.langcode], parsed.country);
          return;
        }
      }
    }
    catch (e) {
      // Ignore and fall through to a live lookup.
    }

    // Cache miss: show the loading message while we look up the best language.
    setSuggestionState('loading');
    // Hide any fallback button left over from a previous attempt.
    toggleGeolocate(false);

    // A lookup is already running from a previous open — don't start another.
    if (detecting) {
      return;
    }
    detecting = true;

    // Resolve exactly once, whichever of these fires first. The safety timeout
    // guarantees the loading message is always replaced, even if the network
    // hangs. Timeout/early paths apply without caching so a later open retries.
    // `cache` is true only for a confident IP result (cached for the session).
    // Unconfident browser-only fallbacks are applied but not cached, and reveal
    // the "Find my location" button so the user can detect their region via the
    // native Geolocation API (e.g. when Firefox blocks the MaxMind script).
    let settled = false;
    const done = function (lang, cache, names) {
      if (settled) {
        return;
      }
      settled = true;
      detecting = false;
      clearTimeout(timeout);
      if (cache) {
        finish(lang, names);
      }
      else {
        geolocateFallback(lang);
      }
    };
    const timeout = setTimeout(function () {
      done(resolveBrowserOnly(settings), false);
    }, 2500);

    loadGeoip2().then(function () {
      if (!window.geoip2) {
        done(resolveBrowserOnly(settings), false);
        return;
      }
      window.geoip2.country(function (geo) {
        const countryCode = geo && geo.country && geo.country.iso_code;
        const region = regionForCountry(countryCode);
        done(resolve(settings, region, wantLangcode(region, navigator.language)), !!countryCode, geo && geo.country && geo.country.names);
      }, function () {
        // MaxMind error (unauthorized domain, no credit, etc.).
        done(resolveBrowserOnly(settings), false);
      });
    }).catch(function () {
      // MaxMind script blocked or failed to load (common in Firefox).
      done(resolveBrowserOnly(settings), false);
    });
  }

  Drupal.behaviors.languageSwitcher = {
    attach: function (context, settings) {
      // Desktop language switcher
      const desktopWrappers = once('language-switcher-desktop', '.language-switcher-wrapper--desktop', context);

      desktopWrappers.forEach(function (wrapper) {
        const toggleButton = wrapper.querySelector('.language-switcher-toggle');
        const panel = wrapper.querySelector('.language-switcher-panel');

        if (!toggleButton || !panel) {
          return;
        }

        // "Find my location" fallback (native Geolocation API).
        const geoButton = wrapper.querySelector('.js-language-geolocate');
        if (geoButton) {
          geoButton.addEventListener('click', function (e) {
            e.preventDefault();
            geolocate();
          });
        }

        // Handle toggle button click
        toggleButton.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();

          const isExpanded = toggleButton.getAttribute('aria-expanded') === 'true';

          if (isExpanded) {
            closePanel();
          } else {
            openPanel();
          }
        });

        // Close panel when clicking outside
        document.addEventListener('click', function (e) {
          if (!e.target.closest('.language-switcher-wrapper--desktop')) {
            closePanel();
          }
        });

        // Close panel on escape key
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && toggleButton.getAttribute('aria-expanded') === 'true') {
            closePanel();
            toggleButton.focus();
          }
        });

        // Same slide as the main menu dropdowns (header.dev.js).
        function openPanel() {
          toggleButton.setAttribute('aria-expanded', 'true');
          panel.removeAttribute('hidden');
          show(panel, slideToggleDefaults);
          detectAndSuggest();
        }

        function closePanel() {
          if (toggleButton.getAttribute('aria-expanded') !== 'true') {
            return;
          }
          toggleButton.setAttribute('aria-expanded', 'false');
          hide(panel, {
            ...slideToggleDefaults,
            onAnimationEnd: function () {
              // Reopened before the slide finished: undo hide()'s display:none.
              if (toggleButton.getAttribute('aria-expanded') === 'true') {
                panel.style.display = 'block';
                return;
              }
              panel.setAttribute('hidden', '');
              panel.removeAttribute('style');
            },
          });
        }
      });

      // Mobile language switcher
      const mobileWrappers = once('language-switcher-mobile', '.language-switcher-wrapper--mobile', context);

      mobileWrappers.forEach(function (wrapper) {
        const toggleButton = wrapper.querySelector('.language-switcher-mobile-toggle');
        const panel = wrapper.querySelector('.language-switcher-mobile-panel');
        const backButton = wrapper.querySelector('.language-switcher-mobile-back');

        if (!toggleButton || !panel) {
          return;
        }

        // "Find my location" fallback (native Geolocation API).
        const geoButton = wrapper.querySelector('.js-language-geolocate');
        if (geoButton) {
          geoButton.addEventListener('click', function (e) {
            e.preventDefault();
            geolocate();
          });
        }

        // Handle toggle button click - slide panel in
        toggleButton.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          slidePanelIn(true);
        });

        // Handle back button click - slide panel out
        if (backButton) {
          backButton.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            slidePanelIn(false);
            toggleButton.focus();
          });
        }

        // Close panel on escape key
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && panel.classList.contains('js-open')) {
            slidePanelIn(false);
            toggleButton.focus();
          }
        });

        function slidePanelIn(open) {
          const delay = 100;

          if (open) {
            panel.classList.remove('hidden');
            panel.classList.add('block');
            toggleButton.setAttribute('aria-expanded', 'true');
            detectAndSuggest();
            setTimeout(function () {
              panel.classList.remove('left-full');
              panel.classList.add('left-0');
              panel.classList.add('js-open');
            }, delay);
          } else {
            panel.classList.remove('left-0');
            panel.classList.add('left-full');
            panel.classList.remove('js-open');
            toggleButton.setAttribute('aria-expanded', 'false');
            setTimeout(function () {
              panel.classList.add('hidden');
              panel.classList.remove('block');
            }, 300);
          }
        }
      });
    }
  };

})(Drupal, drupalSettings, once);
